import { rem } from 'polished'
import React, { MouseEvent, PropsWithChildren } from 'react'
import styled from 'styled-components'
import CloseButton from '../../Common/CloseButton'
import InlineGroup from '../../Common/InlineGroup'
import Heading from '../Typography/Heading'
import cn from 'clsx'
import BodyText from '../Typography/BodyText'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

const Container = styled.div`
  max-width: 100vw;
  box-shadow: ${props => props.theme.shadow.bottom.small};
  border: 1px solid ${props => props.theme.palette.neutral.six};
  border-radius: ${props => props.theme.borderRadius.medium};
  background-color: ${props => props.theme.palette.neutral.eight};
  padding: ${rem(20)};
  min-width: ${rem(400)};
  overflow: auto;
  -webkit-overflow-scrolling: touch;

  &.fit-dropdown-items {
    min-width: unset;
    padding: ${rem(8)};

    > * {
      white-space: nowrap;
    }
  }

  &.fit-auto {
    min-width: unset;
  }
`

const Header = styled(InlineGroup)`
  margin-bottom: ${rem(16)}; /* Margin bottom here because this element is conditional and we do not want the extra space */
`

interface BaseProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  // Use dropdown-items when your content are dropdown items. They have their own padding so we need
  // to specifically cater for this components to work with it
  fit?: 'auto' | 'dropdown-items';
  style?: React.CSSProperties;
}

interface PropsWithTitle extends BaseProps {
  title: string
  subtitle?: string
  onClose?: (e: MouseEvent<HTMLButtonElement>) => void;
}

interface PropsWithNoTitle extends BaseProps {
  title?: undefined | ''
  subtitle?: undefined
  onClose?: undefined
}

type Props = PropsWithTitle | PropsWithNoTitle

const DropdownContainer = React.forwardRef<HTMLDivElement, PropsWithChildren<Props>>((props, ref) => {
  const { children, className, fit, title, subtitle, onClose, ...rest } = props

  return (<Container {...rest} className={cn(className, `fit-${fit}`)} aria-label={title} ref={ref}>
    {!!title && <Header horizontal="between">
      <VerticalSpacer gap={8}>
        <Heading variant="heading6">{title}</Heading>
        {!!subtitle && <BodyText variant="medium" colour="neutral-two">{subtitle}</BodyText>}
      </VerticalSpacer>
      {!!onClose && <CloseButton onClick={onClose}/>}
    </Header>}
    {children}
  </Container>

  )
})

DropdownContainer.displayName = 'DropdownContainer'

export default DropdownContainer
